/**
 * nameAliases — read ``field.name_aliases`` for display and for search
 * attribution (#2842).
 *
 * The twin folds 540 source spellings onto 402 WF field entities. The farmer
 * sees only the survivor, and the spellings they actually recognise — their
 * wiberry Satz label, their FruitForecast range — used to return nothing. This
 * module is the frontend's whole understanding of that attribute.
 *
 * TWO SHAPES, FOREVER. Since #2842 an entry is
 * ``{value, source_system}``; before it, a bare string. 127 WF prod fields still
 * hold bare strings and no migration rewrites them, so an unattributed entry is
 * a supported state that renders WITHOUT a badge rather than with a guessed one.
 *
 * ⚠️ THE ALIAS ILLUSION, which this module exists under. **An alias is
 * descriptive metadata, never a resolution key.** Only the survivor name is
 * hashed into ``field_id``; reading "this name appears over there" as "already
 * converged" previously cost 10 forked fields and 2 bad crosswalk entries. So
 * the UI never presents an alias as interchangeable with the canonical name and
 * never offers it as something to copy: it is always *"system X calls it this"*.
 */

/** The attribute key. Only ``field`` declares it in the ontology. */
export const ALIAS_ATTRIBUTE = 'name_aliases';

export interface NameAlias {
  value: string;
  /** The contributing source system, or null for a pre-#2842 entry. */
  sourceSystem: string | null;
}

function readEntry(raw: unknown): NameAlias | null {
  if (typeof raw === 'string') {
    const value = raw.trim();
    return value ? { value, sourceSystem: null } : null;
  }
  if (!raw || typeof raw !== 'object') return null;
  const record = raw as Record<string, unknown>;
  const value = typeof record.value === 'string' ? record.value.trim() : '';
  if (!value) return null;
  const source = typeof record.source_system === 'string' ? record.source_system.trim() : '';
  return { value, sourceSystem: source || null };
}

/**
 * Parse a raw ``name_aliases`` attribute value.
 *
 * Order is preserved rather than re-sorted: the server already merges the array
 * into a stable ``(value, source_system)`` order, and re-sorting here would
 * quietly diverge from what every other reader sees.
 */
export function parseNameAliases(raw: unknown): NameAlias[] {
  if (!Array.isArray(raw)) return [];
  const parsed: NameAlias[] = [];
  for (const entry of raw) {
    const alias = readEntry(entry);
    if (alias) parsed.push(alias);
  }
  return parsed;
}

/**
 * The aliases a free-text query matched, for the "gefunden über …" attribution
 * on a `/farm` search hit.
 *
 * Returns nothing when the query already matches the canonical ``name``: the row
 * is then explained by its own title and an extra line would be noise. This is
 * ATTRIBUTION only — the matching itself already happens server-side, where
 * ``q`` substring-matches the whole ``attributes`` JSONB cast to text
 * (``twin_reads``/``entity_store``), so a written alias is matchable today and
 * nothing here widens what the search finds.
 */
export function matchedAliases(raw: unknown, query: string, canonicalName?: unknown): NameAlias[] {
  const needle = query.trim().toLowerCase();
  if (!needle) return [];
  const name = typeof canonicalName === 'string' ? canonicalName : '';
  if (name.toLowerCase().includes(needle)) return [];
  return parseNameAliases(raw).filter((alias) => alias.value.toLowerCase().includes(needle));
}
